// ============================================================================
// CrewAhead Maritime Platform - Application Constants
// ============================================================================

// ─── User Roles ──────────────────────────────────────────────────────────────

export const ROLES = {
  SEAFARER: 'seafarer' as const,
  SHORE: 'shore' as const,
  EMPLOYER: 'employer' as const,
  INSTITUTE: 'institute' as const,
  ADMIN: 'admin' as const,
};

export const ROLE_LABELS: Record<string, string> = {
  seafarer: 'Seafarer',
  shore: 'Shore Staff',
  employer: 'Employer / Company',
  institute: 'Training Institute',
  admin: 'Administrator',
};

// ─── Availability Statuses ───────────────────────────────────────────────────

export const AVAILABILITY_STATUSES = {
  AVAILABLE: 'available' as const,
  NOT_AVAILABLE: 'not_available' as const,
  ON_VACATION: 'on_vacation' as const,
  ON_CONTRACT: 'on_contract' as const,
  SHORT_NOTICE: 'short_notice' as const,
  OPEN_TO_OFFERS: 'open_to_offers' as const,
};

export const AVAILABILITY_STATUS_LABELS: Record<string, string> = {
  available: 'Available for Work',
  not_available: 'Not Available',
  on_vacation: 'On Vacation / Leave',
  on_contract: 'Currently on Contract',
  short_notice: 'Available on Short Notice',
  open_to_offers: 'Open to Offers',
};

export const AVAILABILITY_STATUS_COLORS: Record<string, string> = {
  available: 'bg-emerald-500',
  not_available: 'bg-gray-500',
  on_vacation: 'bg-blue-500',
  on_contract: 'bg-amber-500',
  short_notice: 'bg-teal-500',
  open_to_offers: 'bg-sky-500',
};

// ─── Availability Post Statuses (post lifecycle) ─────────────────────────────
// NOTE: These are distinct from AVAILABILITY_STATUS_* above.
// AVAILABILITY_STATUS_* = the seafarer's current availability (available, on_contract, etc.)
// AVAILABILITY_POST_STATUS_* = the status of an availability post (draft, active, filled, etc.)

export const AVAILABILITY_POST_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  filled: 'Position Filled',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
};

export const AVAILABILITY_POST_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-400',
  active: 'bg-emerald-500',
  paused: 'bg-amber-400',
  filled: 'bg-blue-500',
  expired: 'bg-gray-400',
  withdrawn: 'bg-red-400',
};

// ─── Contract Types ──────────────────────────────────────────────────────────

export const CONTRACT_TYPES = {
  PERMANENT: 'permanent' as const,
  TEMPORARY: 'temporary' as const,
  CONTRACT: 'contract' as const,
  ROTATIONAL: 'rotational' as const,
  CASUAL: 'casual' as const,
};

export const CONTRACT_TYPE_LABELS: Record<string, string> = {
  permanent: 'Permanent',
  temporary: 'Temporary',
  contract: 'Fixed Contract',
  rotational: 'Rotational',
  casual: 'Casual / Relief',
};

// ─── Vessel Types ────────────────────────────────────────────────────────────

export const VESSEL_TYPES = {
  CONTAINER_SHIP: 'container_ship' as const,
  BULK_CARRIER: 'bulk_carrier' as const,
  TANKER_CRUDE: 'tanker_crude' as const,
  TANKER_PRODUCT: 'tanker_product' as const,
  TANKER_CHEMICAL: 'tanker_chemical' as const,
  TANKER_LNG: 'tanker_lng' as const,
  TANKER_LPG: 'tanker_lpg' as const,
  GENERAL_CARGO: 'general_cargo' as const,
  RO_RO: 'ro_ro' as const,
  PASSENGER_FERRY: 'passenger_ferry' as const,
  CRUISE_SHIP: 'cruise_ship' as const,
  OFFSHORE_SUPPLY: 'offshore_supply' as const,
  TUG: 'tug' as const,
  DREDGER: 'dredger' as const,
  FISHING_VESSEL: 'fishing_vessel' as const,
  YACHT: 'yacht' as const,
  REEFER: 'reefer' as const,
  CAR_CARRIER: 'car_carrier' as const,
  HEAVY_LIFT: 'heavy_lift' as const,
  LIVESTOCK_CARRIER: 'livestock_carrier' as const,
  BARGE: 'barge' as const,
  OTHER: 'other' as const,
};

export const VESSEL_TYPE_LABELS: Record<string, string> = {
  container_ship: 'Container Ship',
  bulk_carrier: 'Bulk Carrier',
  tanker_crude: 'Crude Oil Tanker',
  tanker_product: 'Product Tanker',
  tanker_chemical: 'Chemical Tanker',
  tanker_lng: 'LNG Carrier',
  tanker_lpg: 'LPG Carrier',
  general_cargo: 'General Cargo',
  ro_ro: 'Ro-Ro Vessel',
  passenger_ferry: 'Passenger Ferry',
  cruise_ship: 'Cruise Ship',
  offshore_supply: 'Offshore / Supply Vessel',
  tug: 'Tugboat',
  dredger: 'Dredger',
  fishing_vessel: 'Fishing Vessel',
  yacht: 'Yacht',
  reefer: 'Reefer Vessel',
  car_carrier: 'Car Carrier',
  heavy_lift: 'Heavy Lift',
  livestock_carrier: 'Livestock Carrier',
  barge: 'Barge',
  other: 'Other',
};

// ─── Document Types ──────────────────────────────────────────────────────────

export const DOCUMENT_TYPES = {
  PASSPORT: 'passport' as const,
  SEAMANS_BOOK: 'seamans_book' as const,
  COC: 'coc' as const,
  GMDSS: 'gmdss' as const,
  MEDICAL_CERTIFICATE: 'medical_certificate' as const,
  FLAG_STATE_ENDORSEMENT: 'flag_state_endorsement' as const,
  VISAS_US: 'visas_us' as const,
  VISAS_SCHENGEN: 'visas_schengen' as const,
  DRUG_ALCOHOL_TEST: 'drug_alcohol_test' as const,
  BST: 'bst' as const,
  SECURITY_AWARENESS: 'security_awareness' as const,
  DESIGNATED_SECURITY_DUTIES: 'designated_security_duties' as const,
  CROWD_MANAGEMENT: 'crowd_management' as const,
  CRISIS_MANAGEMENT: 'crisis_management' as const,
  ADVANCED_FIREFIGHTING: 'advanced_firefighting' as const,
  MEDICAL_FIRST_AID: 'medical_first_aid' as const,
  MEDICAL_CARE: 'medical_care' as const,
  SAR: 'sar' as const,
  FAST_RESCUE_CRAFT: 'fast_rescue_craft' as const,
  ECDIS: 'ecdis' as const,
  OTHER: 'other' as const,
};

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  passport: 'Passport',
  seamans_book: "Seaman's Book",
  coc: 'Certificate of Competency (COC)',
  gmdss: 'GMDSS Certificate',
  medical_certificate: 'Medical Certificate (ENG 1 / ML5)',
  flag_state_endorsement: 'Flag State Endorsement',
  visas_us: 'US Visa (C1/D)',
  visas_schengen: 'Schengen Visa',
  drug_alcohol_test: 'Drug & Alcohol Test',
  bst: 'Basic Safety Training (BST)',
  security_awareness: 'Security Awareness (SSA)',
  designated_security_duties: 'Designated Security Duties (DSD)',
  crowd_management: 'Crowd Management',
  crisis_management: 'Crisis Management',
  advanced_firefighting: 'Advanced Fire Fighting',
  medical_first_aid: 'Medical First Aid',
  medical_care: 'Medical Care Onboard',
  sar: 'Search & Rescue',
  fast_rescue_craft: 'Fast Rescue Craft',
  ecdis: 'ECDIS / Electronic Charts',
  other: 'Other Document',
};

// ─── Certification Types ─────────────────────────────────────────────────────

export const CERTIFICATION_TYPES = {
  DECK_OFFICER: 'deck_officer' as const,
  ENGINE_OFFICER: 'engine_officer' as const,
  ELECTRO_TECHNICAL: 'electro_technical' as const,
  RATING_DECK: 'rating_deck' as const,
  RATING_ENGINE: 'rating_engine' as const,
  GMDSS_OPERATOR: 'gmdss_operator' as const,
  TANKERMAN: 'tankerman' as const,
  STCW_BASIC: 'stcw_basic' as const,
  STCW_ADVANCED: 'stcw_advanced' as const,
  SPECIALIZED: 'specialized' as const,
};

export const CERTIFICATION_TYPE_LABELS: Record<string, string> = {
  deck_officer: 'Deck Officer Certificate',
  engine_officer: 'Engine Officer Certificate',
  electro_technical: 'Electro-Technical Certificate',
  rating_deck: 'Deck Rating Certificate',
  rating_engine: 'Engine Rating Certificate',
  gmdss_operator: 'GMDSS Operator Certificate',
  tankerman: 'Tankerman Certificate',
  stcw_basic: 'STCW Basic Training',
  stcw_advanced: 'STCW Advanced Training',
  specialized: 'Specialized Certificate',
};

// NOTE: Taxonomy-backed lists (ranks, shore positions, company types,
// certification categories, etc.) are served by the Seamaster registry.
// Do not add competing hardcoded label maps here. Use the taxonomy service
// helpers (useTaxonomyCategory, buildTaxonomyLabelMap) instead.

// ─── Seafarer Ranks ──────────────────────────────────────────────────────────

// DEPRECATED: These constants remain only as canonical code identifiers for
// type narrowing and switch statements. They must never be used as a dropdown
// source when taxonomy data is available. Prefer ranks from the taxonomy registry.
export const SEAFARER_RANKS = {
  MASTER: 'master' as const,
  CHIEF_OFFICER: 'chief_officer' as const,
  SECOND_OFFICER: 'second_officer' as const,
  THIRD_OFFICER: 'third_officer' as const,
  DECK_CADET: 'deck_cadet' as const,
  CHIEF_ENGINEER: 'chief_engineer' as const,
  SECOND_ENGINEER: 'second_engineer' as const,
  THIRD_ENGINEER: 'third_engineer' as const,
  FOURTH_ENGINEER: 'fourth_engineer' as const,
  ENGINE_CADET: 'engine_cadet' as const,
  ELECTRO_TECHNICAL_OFFICER: 'electro_technical_officer' as const,
  ELECTRICIAN: 'electrician' as const,
  BOSUN: 'bosun' as const,
  ABLE_SEAFARER_DECK: 'able_seafarer_deck' as const,
  ABLE_SEAFARER_ENGINE: 'able_seafarer_engine' as const,
  ORDINARY_SEAMAN: 'ordinary_seaman' as const,
  WIPER: 'wiper' as const,
  COOK: 'cook' as const,
  STEWARD: 'steward' as const,
  CHIEF_COOK: 'chief_cook' as const,
  MESSMAN: 'messman' as const,
  FITTER: 'fitter' as const,
  MOTORMAN: 'motorman' as const,
  OILER: 'oiler' as const,
  GAS_ENGINEER: 'gas_engineer' as const,
  DP_OPERATOR: 'dp_operator' as const,
  CRANE_OPERATOR: 'crane_operator' as const,
  OTHER: 'other' as const,
};

// DEPRECATED: Do not use as a display source. Prefer buildTaxonomyLabelMap(ranks).
export const SEAFARER_RANK_LABELS: Record<string, string> = {
  master: 'Master / Captain',
  chief_officer: 'Chief Officer',
  second_officer: '2nd Officer',
  third_officer: '3rd Officer',
  deck_cadet: 'Deck Cadet',
  chief_engineer: 'Chief Engineer',
  second_engineer: '2nd Engineer',
  third_engineer: '3rd Engineer',
  fourth_engineer: '4th Engineer',
  engine_cadet: 'Engine Cadet',
  electro_technical_officer: 'Electro-Technical Officer (ETO)',
  electrician: 'Electrician',
  bosun: 'Bosun',
  able_seafarer_deck: 'Able Seafarer - Deck (AB)',
  able_seafarer_engine: 'Able Seafarer - Engine',
  ordinary_seaman: 'Ordinary Seaman (OS)',
  wiper: 'Wiper',
  cook: 'Cook',
  steward: 'Steward',
  chief_cook: 'Chief Cook',
  messman: 'Messman',
  fitter: 'Fitter',
  motorman: 'Motorman',
  oiler: 'Oiler',
  gas_engineer: 'Gas Engineer',
  dp_operator: 'Dynamic Positioning Operator',
  crane_operator: 'Crane Operator',
  other: 'Other Rank',
};

// DEPRECATED: Do not use for department assignment. Prefer taxonomy rank.department.
export const RANK_DEPARTMENTS: Record<string, string> = {
  master: 'Deck',
  chief_officer: 'Deck',
  second_officer: 'Deck',
  third_officer: 'Deck',
  deck_cadet: 'Deck',
  chief_engineer: 'Engine',
  second_engineer: 'Engine',
  third_engineer: 'Engine',
  fourth_engineer: 'Engine',
  engine_cadet: 'Engine',
  electro_technical_officer: 'Electrical',
  electrician: 'Electrical',
  bosun: 'Deck',
  able_seafarer_deck: 'Deck',
  able_seafarer_engine: 'Engine',
  ordinary_seaman: 'Deck',
  wiper: 'Engine',
  cook: 'Catering',
  steward: 'Catering',
  chief_cook: 'Catering',
  messman: 'Catering',
  fitter: 'Engine',
  motorman: 'Engine',
  oiler: 'Engine',
  gas_engineer: 'Engine',
  dp_operator: 'Deck',
  crane_operator: 'Deck',
  other: 'Other',
};

// ─── Shore Positions ─────────────────────────────────────────────────────────

// DEPRECATED: Taxonomy-backed shore positions are served by the registry.
// Keep only as canonical code identifiers; do not use as dropdown source.
export const SHORE_POSITIONS = {
  FLEET_MANAGER: 'fleet_manager' as const,
  TECHNICAL_SUPERINTENDENT: 'technical_superintendent' as const,
  CREWING_MANAGER: 'crewing_manager' as const,
  CREWING_OFFICER: 'crewing_officer' as const,
  QUALITY_MANAGER: 'quality_manager' as const,
  SAFETY_MANAGER: 'safety_manager' as const,
  OPERATIONS_MANAGER: 'operations_manager' as const,
  CHARTERING_MANAGER: 'chartering_manager' as const,
  MARINE_SUPERINTENDENT: 'marine_superintendent' as const,
  PORT_CAPTAIN: 'port_captain' as const,
  SURVEYOR: 'surveyor' as const,
  HR_MANAGER: 'hr_manager' as const,
  RECRUITMENT_SPECIALIST: 'recruitment_specialist' as const,
  OTHER: 'other' as const,
};

export const SHORE_POSITION_LABELS: Record<string, string> = {
  fleet_manager: 'Fleet Manager',
  technical_superintendent: 'Technical Superintendent',
  crewing_manager: 'Crewing Manager',
  crewing_officer: 'Crewing Officer',
  quality_manager: 'Quality & Compliance Manager',
  safety_manager: 'Safety Manager (ISM)',
  operations_manager: 'Operations Manager',
  chartering_manager: 'Chartering Manager',
  marine_superintendent: 'Marine Superintendent',
  port_captain: 'Port Captain',
  surveyor: 'Marine Surveyor',
  hr_manager: 'HR Manager',
  recruitment_specialist: 'Recruitment Specialist',
  other: 'Other Shore Position',
};

// ─── Employer Company Types ──────────────────────────────────────────────────

// DEPRECATED: Company types are taxonomy-backed via the registry.
// Keep only as canonical code identifiers; do not use as dropdown source.
export const EMPLOYER_COMPANY_TYPES = {
  SHIP_OWNER: 'ship_owner' as const,
  SHIP_MANAGER: 'ship_manager' as const,
  CREWING_AGENCY: 'crewing_agency' as const,
  MANNING_AGENCY: 'manning_agency' as const,
  OFFSHORE_OPERATOR: 'offshore_operator' as const,
  CRUISE_OPERATOR: 'cruise_operator' as const,
  PORT_AUTHORITY: 'port_authority' as const,
  SHIPPING_AGENCY: 'shipping_agency' as const,
  CLASSIFICATION_SOCIETY: 'classification_society' as const,
  INSURANCE_PANDI: 'insurance_pandi' as const,
  OTHER: 'other' as const,
};

// DEPRECATED: Prefer buildTaxonomyLabelMap(companyTypes) from the registry.
export const EMPLOYER_COMPANY_TYPE_LABELS: Record<string, string> = {
  ship_owner: 'Ship Owner',
  ship_manager: 'Ship Manager',
  crewing_agency: 'Crewing Agency',
  manning_agency: 'Manning Agency',
  offshore_operator: 'Offshore Operator',
  cruise_operator: 'Cruise Operator',
  port_authority: 'Port Authority',
  shipping_agency: 'Shipping Agency',
  classification_society: 'Classification Society',
  insurance_pandi: 'Insurance / P&I Club',
  other: 'Other Company Type',
};

// ─── Application Statuses ────────────────────────────────────────────────────

export const APPLICATION_STATUSES = {
  APPLIED: 'applied' as const,
  UNDER_REVIEW: 'under_review' as const,
  SHORTLISTED: 'shortlisted' as const,
  INTERVIEW_SCHEDULED: 'interview_scheduled' as const,
  INTERVIEWED: 'interviewed' as const,
  OFFERED: 'offered' as const,
  ACCEPTED: 'accepted' as const,
  REJECTED: 'rejected' as const,
  WITHDRAWN: 'withdrawn' as const,
};

export const APPLICATION_STATUS_LABELS: Record<string, string> = {
  applied: 'Applied',
  under_review: 'Under Review',
  shortlisted: 'Shortlisted',
  interview_scheduled: 'Interview Scheduled',
  interviewed: 'Interviewed',
  offered: 'Offered',
  accepted: 'Accepted',
  rejected: 'Not Selected',
  withdrawn: 'Withdrawn',
};

export const APPLICATION_STATUS_COLORS: Record<string, string> = {
  applied: 'bg-blue-500',
  under_review: 'bg-amber-500',
  shortlisted: 'bg-indigo-500',
  interview_scheduled: 'bg-violet-500',
  interviewed: 'bg-teal-500',
  offered: 'bg-emerald-500',
  accepted: 'bg-green-600',
  rejected: 'bg-red-500',
  withdrawn: 'bg-gray-500',
};

// ─── Job Statuses ────────────────────────────────────────────────────────────

export const JOB_STATUSES = {
  DRAFT: 'draft' as const,
  PUBLISHED: 'published' as const,
  CLOSED: 'closed' as const,
  FILLED: 'filled' as const,
  EXPIRED: 'expired' as const,
};

export const JOB_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  closed: 'Closed',
  filled: 'Filled',
  expired: 'Expired',
};

// ─── Maritime Theme Colors ───────────────────────────────────────────────────

export const COLORS = {
  // Primary brand colors - Deep ocean navy
  PRIMARY: {
    50: '#f0f4f8',
    100: '#d9e2ec',
    200: '#bcccdc',
    300: '#9fb3c8',
    400: '#829ab1',
    500: '#627d98',
    600: '#486581',
    700: '#334e68',
    800: '#243b53',
    900: '#102a43',
    950: '#0a1929',
  },
  // Secondary - Ocean teal
  SECONDARY: {
    50: '#e6f7f5',
    100: '#b3e8e0',
    200: '#80d9cc',
    300: '#4dcab8',
    400: '#26bba5',
    500: '#00a896',
    600: '#008f7f',
    700: '#007668',
    800: '#005d50',
    900: '#004439',
  },
  // Accent - Sunset orange (safety/warning)
  ACCENT: {
    50: '#fff5f0',
    100: '#ffe6d9',
    200: '#ffccbc',
    300: '#ffab91',
    400: '#ff8a65',
    500: '#ff6e40',
    600: '#f4511e',
    700: '#e64a19',
    800: '#d84315',
    900: '#bf360c',
  },
  // Semantic colors
  SUCCESS: '#10b981',
  WARNING: '#f59e0b',
  ERROR: '#ef4444',
  INFO: '#3b82f6',
  // Maritime specific
  OCEAN: '#0077be',
  DEEP_SEA: '#001f3f',
  FOAM: '#e8f4f8',
  SAND: '#f5e6d3',
  CORAL: '#ff7f50',
  GOLD: '#daa520',
  SILVER: '#c0c0c0',
  BRONZE: '#cd7f32',
};

// ─── API Endpoints ───────────────────────────────────────────────────────────

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/token/refresh',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
    ME: '/auth/me',
    SOCIAL_GOOGLE: '/auth/social/google',
    SOCIAL_FACEBOOK: '/auth/social/facebook',
    SOCIAL_LINKEDIN: '/auth/social/linkedin',
  },
  PROFILE: {
    BASE: '/profile',
    DETAIL: '/profile/me',
    UPDATE: '/profile',
    UPLOAD_DOCUMENT: '/profile/documents',
    DELETE_DOCUMENT: (id: string) => `/profile/documents/${id}`,
    SEA_SERVICE: '/profile/sea-service',
    SEA_SERVICE_DETAIL: (id: string) => `/profile/sea-service/${id}`,
    CERTIFICATIONS: '/profile/certifications',
    CERTIFICATION_DETAIL: (id: string) => `/profile/certifications/${id}`,
    // Role-specific profile endpoints
    SEAFARER_DETAIL: '/seafarers/profiles/my-profile',
    SHORE_DETAIL: '/shore/profiles/profile',
  },
  JOBS: {
    LIST: '/jobs',
    ONBOARD: '/jobs/onboard',
    SHORE: '/jobs/shore',
    DETAIL: (id: string) => `/jobs/${id}`,
    APPLY: (id: string) => `/jobs/${id}/apply`,
    APPLICATIONS: '/applications',
    SAVED: '/jobs/saved',
    SAVE: (id: string) => `/jobs/${id}/save`,
    UNSAVE: (id: string) => `/jobs/${id}/unsave`,
    ALERTS: '/jobs/alerts',
    ALERT_DETAIL: (id: string) => `/jobs/alerts/${id}`,
    PUBLIC: '/jobs/public',
  },
  NOTIFICATIONS: {
    LIST: '/notifications',
    DETAIL: (id: string) => `/notifications/${id}`,
    MARK_READ: (id: string) => `/notifications/${id}/mark_read`,
    MARK_ALL_READ: '/notifications/mark_all_read',
    UNREAD_COUNT: '/notifications/unread_count',
    UNREAD_LIST: '/notifications/unread',
    CLEAR_ALL: '/notifications/clear_all',
    PREFERENCES: '/notifications/preferences',
    ADMIN_CREATE: '/notifications/admin_create',
    BULK_CREATE: '/notifications/bulk_create',
  },
  MESSAGES: {
    BASE: '/messages',
    BY_APPLICATION: (applicationId: string) =>
      `/messages?application=${encodeURIComponent(applicationId)}`,
    CONVERSATIONS: '/messaging/conversations',
    CONVERSATION_DETAIL: (id: string) => `/messaging/conversations/${id}`,
    MESSAGES: (conversationId: string) =>
      `/messages?conversation=${encodeURIComponent(conversationId)}`,
  },
  AVAILABILITY: {
    BASE: '/availability/posts',
    LIST: '/availability/posts',
    DETAIL: (id: string) => `/availability/posts/${id}`,
    CREATE: '/availability/posts',
    UPDATE: (id: string) => `/availability/posts/${id}`,
    DELETE: (id: string) => `/availability/posts/${id}`,
    PUBLISH: (id: string) => `/availability/posts/${id}/publish`,
    CONTACT: (id: string) => `/availability/posts/${id}/contact`,
    MY_POSTS: '/availability/posts/my_posts',
    FILTERS: '/availability/posts/filters',
  },
  UPLOAD: {
    PRESIGNED: '/upload/presigned',
    CONFIRM: '/upload/confirm',
  },
  MATCHING: {
    JOBS: '/matching/jobs',
    CANDIDATES: '/matching/candidates',
    SCORE: '/matching/score',
    RECALCULATE: '/matching/recalculate',
  },
  EMPLOYER: {
    COMPANY: '/employers/company',
    COMPANY_DETAIL: (id: string) => `/employers/company/${id}`,
    COMPANY_DOCUMENTS: (id: string) => `/employers/company/${id}/documents`,
    COMPANY_TEAM: (id: string) => `/employers/company/${id}/team`,
    DASHBOARD: '/employers/dashboard',
    SUBSCRIPTION: '/employers/subscription',
  },
  INSTITUTE: {
    PROFILE_ME: '/institute/profile/me',
    PROFILE_UPDATE: '/institute/profile/update',
    OFFERINGS: '/institute/offerings',
    OFFERING_DETAIL: (id: string | number) => `/institute/offerings/${id}`,
    BATCHES: '/institute/batches',
    BATCH_DETAIL: (id: string | number) => `/institute/batches/${id}`,
    ENQUIRIES: '/institute/enquiries',
    ENQUIRY_DETAIL: (id: string | number) => `/institute/enquiries/${id}`,
    RESOURCES: '/institute/resources',
    RESOURCE_DETAIL: (id: string | number) => `/institute/resources/${id}`,
    PUBLIC_RESOURCES: '/institute/resources/public',
    PUBLIC_RESOURCE_DETAIL: (id: string | number) => `/institute/resources/public/${id}`,
    RESOURCE_ENQUIRIES: '/institute/resource-enquiries',
    RESOURCE_ENQUIRY_DETAIL: (id: string | number) => `/institute/resource-enquiries/${id}`,
    PUBLIC_OFFERINGS: '/institute/offerings/public',
    PUBLIC_OFFERING_DETAIL: (id: string | number) => `/institute/offerings/public/${id}`,
    PUBLIC_BATCHES: '/institute/batches/public',
    PUBLIC_BATCH_DETAIL: (id: string | number) => `/institute/batches/public/${id}`,
    PUBLIC_INSTITUTES: '/institute/directory/public',
  },
  ONBOARDING: {
    STEPS: '/onboarding/steps',
    PROGRESS: '/onboarding/progress',
    START: '/onboarding/progress/start',
    SKIP: '/onboarding/progress/skip',
    STATUS: '/onboarding/status',
  },
  CANDIDATES: {
    LIST: '/candidates',
    DETAIL: (id: string) => `/candidates/${id}`,
    FEATURED: '/candidates/featured',
    SHORTLISTED: '/candidates/shortlisted',
    FILTERS: '/candidates/filters',
    SAVE: (id: string) => `/candidates/${id}/save`,
    CONTACT: (id: string) => `/candidates/${id}/contact`,
    REVEAL: '/candidates/reveal',
    REVEAL_STATUS: '/candidates/reveal-status',
  },
  CONTENT: {
    POSTS: '/content/posts',
    POST_DETAIL: (slug: string) => `/content/posts/${slug}`,
    CATEGORIES: '/content/categories',
    TAGS: '/content/tags',
    SOURCES: '/content/sources',
    KNOWLEDGE_HUB: '/content/knowledge-hub',
    RELATED: (slug: string) => `/content/related/${slug}`,
    FAQS: '/content/faqs',
    PAGES: '/content/pages',
    PAGE_DETAIL: (slug: string) => `/content/pages/${slug}`,
    TESTIMONIALS: '/content/testimonials',
    SETTINGS: '/content/settings',
    CONTACT: '/content/contact',
    NEWSLETTER: '/content/newsletter',
    ADMIN: {
      POSTS: '/content/admin/posts',
      POST_DETAIL: (slug: string) => `/content/admin/posts/${slug}`,
      PUBLISH: (slug: string) => `/content/admin/posts/${slug}/publish`,
      UNPUBLISH: (slug: string) => `/content/admin/posts/${slug}/unpublish`,
      ARCHIVE: (slug: string) => `/content/admin/posts/${slug}/archive`,
      AI_REWRITE: '/content/admin/posts/ai-rewrite',
      CATEGORIES: '/content/admin/categories',
      TAGS: '/content/admin/tags',
      SOURCES: '/content/admin/sources',
    },
  },
  ACCOUNTS: {
    ADMIN: {
      USERS: '/accounts/admin/users',
      USER_DETAIL: (id: string | number) => `/accounts/admin/users/${id}`,
      SUSPEND: (id: string | number) => `/accounts/admin/users/${id}/suspend`,
      REACTIVATE: (id: string | number) => `/accounts/admin/users/${id}/reactivate`,
      CHANGE_ROLE: (id: string | number) => `/accounts/admin/users/${id}/change-role`,
      APPROVE_INSTITUTE: (id: string | number) => `/accounts/admin/users/${id}/approve-institute`,
      AUDIT_LOG: (id: string | number) => `/accounts/admin/users/${id}/audit-log`,
      SITE_CONFIG: '/accounts/admin/settings/config',
    },
  },
  PUBLIC: {
    STATS: '/public/stats',
  },
  VESSEL: {
    LOOKUP: '/vessels/lookup',
    LIVE_MAP: '/vessels/live-map',
    RISK_ALERTS: '/vessels/risk-alerts',
    HISTORY: '/vessels/history',
    STATS: '/vessels/stats',
    DETAIL: (imo: string) => `/vessels/${imo}`,
    VOYAGE_REPLAY: (imo: string) => `/vessels/${imo}/voyage-replay`,
    PSC_REPORTS: (imo: string) => `/vessels/${imo}/psc`,
    PSC_REPORT_DETAIL: (imo: string, reportId: string) => `/vessels/${imo}/psc/${reportId}`,
    ACKNOWLEDGE_ALERT: (id: string) => `/vessels/risk-alerts/${id}/acknowledge`,
  },
  PAYMENTS: {
    SUBSCRIPTION_PLANS: '/payments/subscriptions/plans',
    SUBSCRIPTION_PLANS_BY_TYPE: '/payments/subscriptions/plans/by_type',
    SUBSCRIPTIONS: '/payments/subscriptions',
    SUBSCRIPTION_CANCEL: (id: string | number) => `/payments/subscriptions/${id}/cancel`,
    MY_SUBSCRIPTIONS: '/payments/subscriptions/my_subscriptions',
    PAYMENTS: '/payments/payments',
    PAYMENT_INITIATE: (id: string | number) => `/payments/payments/${id}/initiate`,
    PAYMENT_VERIFY: (id: string | number) => `/payments/payments/${id}/verify`,
    PAYMENT_REFUND: (id: string | number) => `/payments/payments/${id}/refund`,
    MY_PAYMENTS: '/payments/payments/my_payments',
    INVOICES: '/payments/invoices',
    INVOICE_DOWNLOAD: (id: string | number) => `/payments/invoices/${id}/download`,
    WALLET: '/payments/wallet',
    WALLET_TRANSACTIONS: '/payments/wallet/transactions',
    WALLET_CREDIT: '/payments/wallet/credit',
    WALLET_DEBIT: '/payments/wallet/debit',
  },
  FILES: {
    DOCUMENTS: '/documents',
    DOCUMENT_DETAIL: (id: string) => `/documents/${id}`,
    UPLOAD: '/documents/upload',
    UPLOAD_URL: '/documents/upload-url',
  },
  SEAFARER: {
    PROFILES: '/seafarers/profiles',
    PROFILE_DETAIL: (id: string) => `/seafarers/profiles/${id}`,
    MY_PROFILE: '/seafarers/profiles/my-profile',
    SEA_SERVICE: '/seafarers/sea-service',
    SEA_SERVICE_DETAIL: (id: string) => `/seafarers/sea-service/${id}`,
    CERTIFICATIONS: '/seafarers/certifications',
    CERTIFICATION_DETAIL: (id: string) => `/seafarers/certifications/${id}`,
    DOCUMENTS: '/seafarers/documents',
    DOCUMENT_DETAIL: (id: string) => `/seafarers/documents/${id}`,
    DOCUMENT_DOWNLOAD: (id: string) => `/seafarers/documents/${id}/download`,
    VISAS: '/seafarers/visas',
    VISA_DETAIL: (id: string) => `/seafarers/visas/${id}`,
    SEARCH: '/seafarers/search',
    DASHBOARD: '/seafarers/profile/dashboard',
  },
  SHORE: {
    PROFILES: '/shore/profiles',
    PROFILE_DETAIL: (id: string) => `/shore/profiles/${id}`,
    MY_PROFILE: '/shore/profile',
    MY_PROFILE_CERTIFICATIONS: '/shore/profiles/my-profile/certifications',
    MY_PROFILE_CERTIFICATION_DETAIL: (id: string) => `/shore/profiles/my-profile/certifications/${id}`,
    EXPERIENCE: '/shore/experience',
    EXPERIENCE_DETAIL: (id: string) => `/shore/experience/${id}`,
    DOCUMENTS: '/shore/documents',
    DOCUMENT_DETAIL: (id: string) => `/shore/documents/${id}`,
    PREFERENCES: '/shore/preferences',
    PREFERENCE_DETAIL: (id: string) => `/shore/preferences/${id}`,
    SEARCH: '/shore/search',
    DASHBOARD: '/shore/profile/dashboard',
  },
  CAREER_TRANSITION: {
    PROFILE: '/career-transition/profile',
    TARGET_POSITIONS: '/career-transition/target-positions',
    ACTIVATE: '/career-transition/activate',
    DEACTIVATE: '/career-transition/deactivate',
    READINESS: '/career-transition/readiness',
    CAREER_PATH: '/career-transition/career-path',
    SKILL_GAPS: '/career-transition/skill-gaps',
    RECOMMENDED_COURSES: '/career-transition/recommended-courses',
    SUMMARY: '/career-transition/summary',
    SHORE_JOBS: '/career-transition/shore-jobs',
    BARRIERS: '/career-transition/barriers',
    CALCULATE: '/career-transition/calculate',
    SEA_TO_SHORE_PATHS: '/career-transition/sea-to-shore-paths',
    SHORE_TO_SEA_PATHS: '/career-transition/shore-to-sea-paths',
    CAREER_PATHS_BROWSE: '/career-transition/career-paths',
    TRANSFERABLE_SKILLS: '/career-transition/transferable-skills',
    RECOMMENDATIONS: '/career-transition/recommendations',
    TARGET_ROLES: '/career-transition/target-roles',
    ROLE_MATRIX: (roleCode: string) => `/career-transition/matrix/${roleCode}`,
    GENERATE_PATH: '/career-transition/generate-path',
    PROMOTION_READINESS: '/career-transition/promotion-readiness',
  },
};

// ─── Pagination Defaults ─────────────────────────────────────────────────────

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  LIMIT_OPTIONS: [10, 20, 50, 100],
};

// ─── Validation Rules ────────────────────────────────────────────────────────

export const VALIDATION_RULES = {
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  NAME_MIN_LENGTH: 1,
  NAME_MAX_LENGTH: 100,
  BIO_MAX_LENGTH: 5000,
  JOB_DESCRIPTION_MAX_LENGTH: 20000,
  COVER_LETTER_MAX_LENGTH: 10000,
  NOTES_MAX_LENGTH: 2000,
  IMO_NUMBER_LENGTH: 7,
  PHONE_MIN_LENGTH: 5,
  PHONE_MAX_LENGTH: 20,
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

// ─── LocalStorage Keys ───────────────────────────────────────────────────────

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'crew_ahead_access_token',
  REFRESH_TOKEN: 'crew_ahead_refresh_token',
  USER: 'crew_ahead_user',
  THEME: 'crew_ahead_theme',
  LANGUAGE: 'crew_ahead_language',
  ONBOARDING_COMPLETE: 'crew_ahead_onboarding',
  LAST_ROUTE: 'crew_ahead_last_route',
  SETTINGS: 'crew_ahead_settings',
  SAVED_FILTERS: 'crew_ahead_saved_filters',
  EMAIL_BANNER_DISMISSED: 'crew_ahead_email_banner_dismissed',
};

// ─── Cookie Names ────────────────────────────────────────────────────────────

export const COOKIE_NAMES = {
  ACCESS_TOKEN: 'ca_at',
  REFRESH_TOKEN: 'ca_rt',
  SESSION_ID: 'ca_sid',
  THEME: 'ca_theme',
};

// ─── Route Paths ─────────────────────────────────────────────────────────────

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_EMAIL: '/verify-email',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  PROFILE_EDIT: '/profile/edit',
  SEA_SERVICE: '/profile/sea-service',
  CERTIFICATIONS: '/profile/certifications',
  DOCUMENTS: '/profile/documents',
  JOBS: '/jobs',
  JOB_DETAIL: (id: string) => `/jobs/${id}`,
  APPLICATIONS: '/applications',
  SAVED_JOBS: '/saved-jobs',
  JOB_ALERTS: '/job-alerts',
  MESSAGES: '/messages',
  NOTIFICATIONS: '/notifications',
  SETTINGS: '/settings',
  // Employer routes
  EMPLOYER_DASHBOARD: '/employer',
  POST_JOB: '/employer/jobs/post',
  MANAGE_JOBS: '/employer/jobs',
  CANDIDATES: '/employer/candidates',
  CANDIDATES_BROWSE: '/candidates',
  COMPANY_PROFILE: '/employer/company',
  // Institute routes
  INSTITUTE_DASHBOARD: '/institute',
  INSTITUTE_PENDING: '/institute/pending',
  // Admin routes
  ADMIN_DASHBOARD: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_CONTENT: '/admin/content',
  ADMIN_REPORTS: '/admin/reports',
  // Career & transition routes (canonical under /seafarer)
  CAREER: '/seafarer/career',
  CAREER_TRANSITION: '/seafarer/career/transition',
  CAREER_PATH: '/seafarer/career/path',
  CAREER_COURSES: '/seafarer/career/courses',
  // Availability routes
  AVAILABILITY_POSTS: '/seafarer/availability',
  AVAILABILITY_NEW: '/seafarer/availability/new',
  // Matching routes
  MATCHED_JOBS: '/dashboard/matched-jobs',
};

// ─── Route Guards ────────────────────────────────────────────────────────────

export const PROTECTED_ROUTES = [
  '/dashboard',
  '/profile',
  '/applications',
  '/saved-jobs',
  '/job-alerts',
  '/messages',
  '/notifications',
  '/settings',
  '/employer',
  '/admin',
];

export const ROLE_BASED_ROUTES: Record<string, string[]> = {
  seafarer: ['/seafarer', '/profile', '/applications', '/saved-jobs', '/job-alerts', '/messages', '/notifications', '/settings'],
  shore: ['/shore', '/profile', '/applications', '/saved-jobs', '/job-alerts', '/messages', '/notifications', '/settings'],
  employer: ['/employer', '/company-profile', '/post-job', '/manage-jobs', '/candidates', '/messages', '/notifications', '/settings'],
  admin: ['/admin'],
};

export const PUBLIC_ROUTES = ['/', '/login', '/logout', '/register', '/forgot-password', '/reset-password', '/verify-email'];

// ─── Public / Legal Page Shared Constants ────────────────────────────────────

export const CONTACT_EMAIL = "crewsahead@gmail.com";
export const LEGAL_PAGE_LAST_UPDATED = "2 July 2026";

